import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Eye, Globe2 } from "lucide-react";
import { useI18n } from "../lib/i18n";

const sessionId = (() => {
  let id = sessionStorage.getItem("fv_sid");
  if (!id) { id = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem("fv_sid", id); }
  return id;
})();

export const useAnalytics = (channelId) => {
  const [stats, setStats] = useState(null);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!channelId) return;
    const track = () => api.post("/analytics/track", { channel_id: channelId, session_id: sessionId, path: window.location.pathname }).catch(() => {});
    const refresh = () => api.get(`/analytics/stream/${channelId}`).then(r => setStats(r.data)).catch(() => {});
    track();
    refresh();
    tickRef.current = setInterval(() => { track(); refresh(); }, 15000);
    return () => clearInterval(tickRef.current);
  }, [channelId]);

  return stats;
};

export const LiveViewerChart = ({ stats }) => {
  const { t } = useI18n();
  if (!stats) return null;
  return (
    <div className="glass rounded-xl p-4" data-testid="live-viewer-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-[color:var(--accent)]" />
          <span className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]">{t("live.audience")}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="font-display text-3xl text-[color:var(--accent)]" data-testid="live-viewer-count">{stats.live_viewers}</span>
          <span className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]">{t("live.watching")}</span>
        </div>
      </div>
      <div className="h-32 -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={stats.timeline} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="#1d3a41" vertical={false} />
            <XAxis dataKey="time" tick={{ fill: "#7a9098", fontSize: 10 }} interval={4} />
            <YAxis tick={{ fill: "#7a9098", fontSize: 10 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: "#0f1f23", border: "1px solid #1d3a41", borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: "#f0b429" }}
            />
            <Line type="monotone" dataKey="viewers" stroke="#f0b429" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {stats.top_countries?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[color:var(--border)]">
          <div className="flex items-center gap-2 mb-2">
            <Globe2 className="w-3 h-3 text-[color:var(--text-muted)]" />
            <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{t("live.topcountries")}</span>
          </div>
          <ul className="space-y-1">
            {stats.top_countries.slice(0, 5).map(c => (
              <li key={c.country} className="flex justify-between text-xs" data-testid={`country-${c.country}`}>
                <span>{c.country}</span>
                <span className="font-mono text-[color:var(--text-muted)]">{c.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
